import React, { FC, useMemo, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Switch, Empty, Tooltip as AntdTooltip, Button } from "antd";
import { generateColors } from "./colors";
import styles from "./charts.module.css";
import { InfoCircleOutlined } from "@ant-design/icons";

const {
  ["container"]: chartContainerClassName,
  ["chart-title"]: chartTitleClassName,
  ["chart-wrapper"]: chartWrapperClassName,
  ["empty-container"]: emptyContainerClassName,
  ["controls-container"]: controlsContainerClassName,
  ["legend-container"]: legendContainerClassName,
  ["legend"]: legendClassName,
  ["legend-item"]: legendItemClassName,
  ["legend-item-color"]: legendItemColorClassName,
} = styles;

interface CountByField {
  __typename?: "CountByField";
  field: string;
  id: number | null;
  count: number;
}

interface TimeSeriesUsageData {
  __typename?: "TimeSeriesUsageData";
  timeLabelString: string;
  totalCount: number;
  [key: string]: any;
}

interface CountLineChartProps {
  data: TimeSeriesUsageData[] | undefined;
  title: string;
  explanatoryText?: string;
  width?: number;
  height?: number;
  xAxisDataKey?: string;
  yAxisLabel?: string;
  countsArrayName: string;
  visibleFields?: string[];
}

const CountLineChart: FC<CountLineChartProps> = ({
  data,
  title,
  explanatoryText,
  width = 600,
  height = 400,
  xAxisDataKey = "timeLabelString",
  yAxisLabel = "Count",
  countsArrayName,
  visibleFields,
}) => {
  const [isCumulative, setIsCumulative] = useState(false);
  const [hiddenLines, setHiddenLines] = useState<Set<string>>(new Set());
  const [hoveredLegendItem, setHoveredLegendItem] = useState<string | null>(null);

  const allFields = useMemo(() => {
    const fieldsSet = new Set<string>();
    data?.forEach((item) => {
      const countsArray = Array.isArray(item[countsArrayName])
        ? (item[countsArrayName] as CountByField[])
        : [];
      countsArray.forEach((count) => {
        if (!visibleFields || visibleFields.includes(count.field)) {
          fieldsSet.add(count.field);
        }
      });
    });
    return Array.from(fieldsSet);
  }, [data, countsArrayName, visibleFields]);

  const areAllLinesVisible = useMemo(() => hiddenLines.size === 0, [hiddenLines]);
  const areAllLinesHidden = useMemo(
    () => hiddenLines.size === allFields.length,
    [hiddenLines, allFields]
  );

  const processedData = useMemo(() => {
    if (!data) return [];

    const cumulativeSums: { [key: string]: number } = {};

    return data.map((item) => {
      const result: { [key: string]: string | number } = {
        [xAxisDataKey]: item[xAxisDataKey],
      };

      allFields.forEach((field) => {
        if (isCumulative) {
          result[field] = cumulativeSums[field] || 0;
        } else {
          result[field] = 0;
        }
      });

      const countsArray = Array.isArray(item[countsArrayName])
        ? (item[countsArrayName] as CountByField[])
        : [];
      countsArray.forEach((count) => {
        if (!visibleFields || visibleFields.includes(count.field)) {
          if (isCumulative) {
            cumulativeSums[count.field] = (cumulativeSums[count.field] || 0) + count.count;
            result[count.field] = cumulativeSums[count.field];
          } else {
            result[count.field] = count.count;
          }
        }
      });

      return result;
    });
  }, [data, xAxisDataKey, countsArrayName, visibleFields, isCumulative, allFields]);

  const toggleLine = (lineKey: string) => {
    setHiddenLines((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(lineKey)) {
        newSet.delete(lineKey);
      } else {
        newSet.add(lineKey);
      }
      return newSet;
    });
  };

  const showAllLines = () => {
    setHiddenLines(new Set());
  };

  const hideAllLines = () => {
    setHiddenLines(new Set(allFields));
  };

  const colors = useMemo(() => generateColors(allFields.length), [allFields.length]);

  if (!data || data.length === 0) {
    return (
      <div className={chartContainerClassName} style={{ width, height }}>
        <h2 className={chartTitleClassName}>
          {title}{" "}
          {explanatoryText && (
            <AntdTooltip placement="top" title={explanatoryText}>
              <InfoCircleOutlined style={{ paddingLeft: "5px" }} />
            </AntdTooltip>
          )}
        </h2>
        <div className={chartWrapperClassName}>
          <div className={emptyContainerClassName}>
            <Empty description="No data" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={chartContainerClassName} style={{ width, height }}>
      <h2 className={chartTitleClassName}>
        {`${title}${isCumulative ? " (Cumulative)" : ""}`}{" "}
        {explanatoryText && (
          <AntdTooltip placement="top" title={explanatoryText}>
            <InfoCircleOutlined style={{ paddingLeft: "5px" }} />
          </AntdTooltip>
        )}
      </h2>
      <div className={controlsContainerClassName}>
        <div>
          <Switch checked={isCumulative} onChange={() => setIsCumulative(!isCumulative)} />
          <span style={{ marginLeft: "10px" }}>Cumulative</span>
        </div>
        <Button onClick={showAllLines} disabled={areAllLinesVisible} style={{ margin: "0 15px" }}>
          Show All
        </Button>
        <Button onClick={hideAllLines} disabled={areAllLinesHidden}>
          Hide All
        </Button>
      </div>
      <div className={chartWrapperClassName}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={processedData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={xAxisDataKey} />
            <YAxis label={{ value: yAxisLabel, angle: -90, position: "insideLeft" }} />
            <Tooltip />
            {allFields.map((field, index) => (
              <Line
                key={field}
                type="monotone"
                dataKey={field}
                name={field}
                stroke={colors[index % colors.length]}
                activeDot={{ r: 8 }}
                hide={hiddenLines.has(field)}
                connectNulls={true}
                strokeWidth={2}
                animationBegin={100}
                animationDuration={800}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className={legendContainerClassName}>
        <div className={legendClassName}>
          {allFields.map((field, index) => (
            <div
              key={index}
              className={legendItemClassName}
              style={{
                cursor: "pointer",
              }}
              onClick={() => toggleLine(field)}
              onMouseEnter={() => setHoveredLegendItem(field)}
              onMouseLeave={() => setHoveredLegendItem(null)}
            >
              <AntdTooltip
                title={`Click to ${hiddenLines.has(field) ? "show" : "hide"}`}
                open={hoveredLegendItem === field}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div
                    className={legendItemColorClassName}
                    style={{
                      backgroundColor: colors[index % colors.length],
                      opacity: hiddenLines.has(field) ? 0.5 : 1,
                    }}
                  />
                  <span
                    style={{
                      textDecoration: hiddenLines.has(field) ? "line-through" : "none",
                      color: hiddenLines.has(field) ? "#999" : "inherit",
                    }}
                  >
                    {field}
                  </span>
                </div>
              </AntdTooltip>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CountLineChart;
